import { useIntl } from 'react-intl'
import * as Yup from 'yup'
import { Formik, Form, Field } from 'formik'
import TextInput from '../../widgets/components/Input/TextInput'
import { SelectInput } from '../../widgets/components/Input/SelectInput'
import TextArea from '../../widgets/components/Input/TextArea'
import { BasicButton } from '../../widgets/components/UI/BasicButton'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { CustomButton } from '../../widgets/components/UI/CustomButton'
import { timeZoneOptions } from '../../profile/core/_constants'
import { useInvestorDatabase } from '../../../../app/modules/investor-database/core/InvestorContext'
import { activityInitialValues } from '../core/_constants'
import { useAuth } from '../../auth'
import { createActivity, getAllInvestorUsers } from '../core/_requests'
import { toast } from 'react-toastify'
import { FileUpload } from '../../widgets/components/FileUpload'
import Select from 'react-select'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { ToolTipUIForModal } from '../../widgets/components/UI/ToolTipUIForModal'
import brandingConfig from '../../../../config/brandingConfig'

export const CreateAllActivity = ({
  setModalShow,
  allInvestors,
  allUsers,
  type,
  companyId,
  investorId,
  getActivity,
  selectedInvestorUser,
  roundId,
  modalShow,
  onSearchChange,
}: {
  setModalShow: Dispatch<SetStateAction<boolean>>
  allInvestors: any
  allUsers: any
  type: string
  companyId: number
  investorId?: number
  getActivity?: () => void
  selectedInvestorUser?: { label: string; value: number } | undefined
  roundId?: number
  modalShow?: boolean
  onSearchChange?: (value: string) => void
}) => {
  const { formatMessage } = useIntl()
  const [loading, setLoading] = useState(false)
  const [allDay, setAllDay] = useState(false)
  const [formValues, setFormValues] = useState<any>(null)
  const [modelStatus, setModelStatus] = useState<boolean>(false)
  const [documentName, setDocumentName] = useState<string>()
  const { currentUser } = useAuth()
  const [investorUser, setInvestorUser] = useState<any>()
  const [selectedInvestor, setSelectedInvestor] = useState<number>()
  const [selectedValue, setSelectedValue] = useState<{ label: string; value: number }[]>([])
  const [errorInvestorUser, setErrorInvestorUser] = useState<string>()
  const { setRefreshFuncActivityLog } = useInvestorDatabase()
  const hasRunRef = useRef(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)

  const intializeActiveSchema = Yup.object().shape({
    title: Yup.string().required(formatMessage({ id: 'Title is required' })),
    selectInvestor: Yup.string().required(formatMessage({ id: 'Investor should be selected' })),
    assignHost: Yup.string(),
    fromDate: Yup.date()
      .required(formatMessage({ id: 'From date required' }))
      .nullable(),
    fromTime: Yup.string()
      .required(formatMessage({ id: 'From time required' }))
      .nullable(),
    toDate: Yup.date()
      .required(formatMessage({ id: 'To date required' }))
      .nullable(),
    toTime: Yup.string()
      .required(formatMessage({ id: 'To time required' }))
      .nullable(),
    timeZone: Yup.string().required(formatMessage({ id: 'Timezone is required' })),
    guest: Yup.string(),
    meetingLink: Yup.string().url(),
    meetingNotes: Yup.string(),
    documentId: Yup.string(),
  })
  useEffect(() => {
    if (investorId) {
      activityInitialValues.selectInvestor = investorId.toString()
      setSelectedInvestor(investorId)
      fetchInvestorUserFromInvestorId(investorId)
    }
  }, [investorId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const storedTimeZone = localStorage.getItem('timeZone')
    const selectedTimeZone = timeZoneOptions.find((timeZone) => {
      return timeZone.value === storedTimeZone
    })
    if (selectedTimeZone) {
      activityInitialValues.timeZone = selectedTimeZone.value
    } else {
      activityInitialValues.timeZone = 'UTC+05:30'
    }
  }, [])

  useEffect(() => {
    const time = new Date().toLocaleDateString(brandingConfig.defaultLocale).split('/')
    let date
    if (+time[0] > 9) {
      date = time[2] + '-' + time[0] + '-' + time[1]
    } else {
      date = time[2] + '-0' + time[0] + '-' + time[1]
    }

    if (type === 'tasks' || type === 'notes' || type === 'documents') {
      var currentHour = new Date().getHours()
      var currentMinutes = new Date().getMinutes()
      var futureTime = new Date(new Date().getTime() + 15 * 60000)
      var futureHour = futureTime.getHours()
      var futureMinutes = futureTime.getMinutes()
      setFormValues({
        ...activityInitialValues,
        fromDate: date,
        fromTime: `${currentHour}:${currentMinutes}`,
        toDate: date,
        toTime: `${futureHour}:${futureMinutes}`,
      })
    } else {
      setFormValues({
        ...activityInitialValues,
        fromDate: date,
        fromTime: '',
        toDate: date,
        toTime: '',
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = () => {
    setModelStatus(true)
  }
  const handleClose = () => {
    setModelStatus(false)
  }

  const convertToUTC = (date: any, offset: any) => {
    const sign = offset[0] === '-' ? '-' : '+'
    const [hours, minutes] = offset.slice(1).split(':')
    const offsetHours = String(Math.abs(parseInt(hours, 10))).padStart(2, '0')
    const offsetMinutes = String(Math.abs(parseInt(minutes, 10))).padStart(2, '0')
    const offsetString = `${sign}${offsetHours}:${offsetMinutes}`
    const local = `${date.slice(0, 10)}T${date.slice(11)}${offsetString}`
    return new Date(local).toISOString()
  }

  const onSubmit = async (values: any) => {
    const offsetMatch = values?.timeZone?.match(/([+-]\d{2}:\d{2})/)
    let extractedOffset
    if (offsetMatch === null) {
      extractedOffset = '00:00'
    } else {
      extractedOffset = offsetMatch[1]
    }
    const startActivityTimeUtc = convertToUTC(
      `${values.fromDate} ${values.fromTime}`,
      extractedOffset
    )
    const endActivityTimeUtc = convertToUTC(`${values.toDate} ${values.toTime}`, extractedOffset)
    setLoading(true)

    if (selectedValue.length === 0) {
      setLoading(false)
      setErrorInvestorUser(formatMessage({ id: 'Investor User is required' }))
      return
    }
    const investorUserIds = selectedValue.map((e: { value: number }) => e.value)

    let data: any = {}
    data.activityType = type
    data.companyId = companyId
    data.status = 'pending'
    data.title = values.title
    data.investorId = +values.selectInvestor
    data.startActivityTime = startActivityTimeUtc
    data.endActivityTime = endActivityTimeUtc
    data.timeZone = values.timeZone
    data.investorUserId = investorUserIds
    if (values.guest.length > 0) {
      data.guest = values.guest
    }
    if (values.documentId) {
      data.documentId = +values.documentId
    }
    data.meetingLink = values.meetingLink
    data.meetingNotes = values.meetingNotes
    if (values.assignHost) {
      data.host = +values.assignHost
    } else {
      data.host = currentUser?.userId
    }
    const {
      data: { success, errors },
    } = await createActivity(data)
    if (success) {
      setLoading(false)
      setModalShow(false)
      if (investorId) {
        getActivity!()
      }
      toast.success(formatMessage({ id: 'Activity created successfully' }))
      setRefreshFuncActivityLog(true)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.CRMCreateActivitySuccessful,
          action: 'normal',
          metadata: {
            investor_user_id: investorUserIds,
            activity_type: type,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            title: values.title,
          },
        },
      })
    } else {
      setLoading(false)
      errors.forEach((error: string) => {
        toast.error(formatMessage({ id: error }))
      })
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.CRMCreateActivityFailed,
          action: 'normal',
          metadata: {
            investor_user_id: investorUserIds,
            activity_type: type,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            title: values.title,
            error: errors[0],
          },
        },
      })
    }
    try {
    } catch (error) {
      setLoading(false)
      console.log(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.CRMCreateActivityFailed,
          action: 'normal',
          metadata: {
            investor_user_id: investorUserIds,
            activity_type: type,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            title: values.title,
            error: error,
          },
        },
      })
    }
  }

  const fetchInvestorUserFromInvestorId = async (investorId: number) => {
    setIsSearching(true)
    const {
      data: { data: investorUsers, success },
    } = await getAllInvestorUsers(investorId, roundId!)
    if (success) {
      const investorUserData = investorUsers.map(
        (investorUser: { name: string; investorUserId: number }) => {
          return { label: investorUser.name, value: investorUser.investorUserId }
        }
      )
      setInvestorUser(investorUserData)
      setIsSearching(false)
    }
  }
  const handleChangeInvestoruser = (selectedOptions: any) => {
    setSelectedValue(selectedOptions)
    setErrorInvestorUser('')
  }
  useEffect(() => {
    if (selectedInvestorUser && investorUser && !hasRunRef.current) {
      setSelectedValue([selectedInvestorUser])
      setErrorInvestorUser('')
      hasRunRef.current = true
    }
  }, [investorUser, selectedInvestorUser])

  useEffect(() => {
    if (selectedInvestor && roundId) {
      setSelectedValue([])
      fetchInvestorUserFromInvestorId(+selectedInvestor)
    }
  }, [selectedInvestor]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (modalShow === false) {
      setInvestorUser([])
    }
  }, [modalShow])

  return (
    <>
      <Formik
        validationSchema={intializeActiveSchema}
        initialValues={formValues || activityInitialValues}
        enableReinitialize={true}
        onSubmit={onSubmit}
      >
        {(formik) => {
          return (
            <Form className='w-100 col-12 mt-5'>
              <div className=' row'>
                <div className='col-12 col-lg-6 col-xl-6 col-md-6'>
                  <div className='mx-2'>
                    <TextInput
                      fieldType={'text'}
                      label={formatMessage({ id: 'Add Title' })}
                      fieldName={'title'}
                      placeholder={formatMessage({
                        id: 'Enter activity title',
                      })}
                      formik={formik}
                      toolTipText={formatMessage({
                        id: 'GLOBAL.TOOLTIP.ACTIVITY.TITLE',
                      })}
                      isTooltipNotRequired={false}
                      isStarRequired={true}
                      isTooltipForModal
                    />
                  </div>
                </div>
                <div className='col-12 col-lg-6 col-xl-6 col-md-6'>
                  <div className='mx-2 mb-7'>
                    <div className='d-flex'>
                      <p className='mb-1 font-size-13 form-label text-dark'>
                        {formatMessage({ id: 'Select a Investor' })}*{' '}
                      </p>
                      <ToolTipUIForModal
                        tooltipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ACTIVITY.SELECT_INVESTOR',
                        })}
                      />
                    </div>
                    <Select
                      name={'selectInvestor'}
                      value={allInvestors?.find((option: any) => option.value === selectedInvestor)}
                      onChange={(option: any) => {
                        formik.setFieldValue('selectInvestor', option?.value)
                        setSelectedInvestor(+option?.value)
                      }}
                      onBlur={formik.handleBlur}
                      options={allInvestors}
                      placeholder={formatMessage({ id: 'Select Investor' })}
                      onInputChange={onSearchChange}
                      isSearchable={true}
                      classNamePrefix='react-select'
                    />
                  </div>
                </div>
              </div>
              <div
                className={`row justify-content-center justify-content-md-between align-items-center ${type === 'tasks' || type === 'notes' || type === 'documents' ? 'd-none' : ''
                  }`}
              >
                <div className='col-12'>
                  <label className='ps-2 font-size-13 mb-2 mb-md-0'>
                    {formatMessage({ id: 'Select time' })}
                    <ToolTipUIForModal tooltipText={formatMessage({ id: 'Select Meeting time' })} />
                  </label>
                </div>
                <div className={`row align-items-center px-0`}>
                  <div className='col-lg-2 col-md-2 col-xl-2 col-12 height-42px px-0 ps-4 ps-md-0'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      onChange={(isChecked) => {
                        if (isChecked.target.checked) {
                          formik.setFieldValue('fromTime', '00:01')
                          formik.setFieldValue('toTime', '23:59')
                          setAllDay(isChecked.target.checked)
                        } else {
                          formik.setFieldValue('fromTime', '')
                          formik.setFieldValue('toTime', '')
                          setAllDay(isChecked.target.checked)
                        }
                      }}
                    />
                    <label className='ms-3 fw-semibold'>{formatMessage({ id: 'All day' })} </label>
                  </div>
                  <div className='col-lg-2 col-md-2 col-xl-2 col-12 py-1'>
                    <TextInput
                      fieldType={'date'}
                      fieldName={'fromDate'}
                      formik={formik}
                      placeholder=''
                      isTooltipNotRequired={true}
                      isWidthNotRequired={false}
                      isTooltipForModal
                    />
                  </div>
                  <div className='col-lg-2 col-md-2 col-xl-2 col-12 py-1 reduce_padding'>
                    <TextInput
                      fieldType={'time'}
                      fieldName={'fromTime'}
                      formik={formik}
                      placeholder=''
                      isTooltipNotRequired={true}
                      isWidthNotRequired={false}
                      isDisabled={allDay}
                      isTooltipForModal
                    />
                  </div>

                  <div className='col-lg-2 col-md-2 col-xl-2 col-12 py-1'>
                    <TextInput
                      fieldType={'date'}
                      fieldName={'toDate'}
                      formik={formik}
                      placeholder=''
                      isTooltipNotRequired={true}
                      isWidthNotRequired={false}
                      isTooltipForModal
                    />
                  </div>
                  <div className='col-lg-2 col-md-2 col-xl-2 col-12 py-1 reduce_padding'>
                    <TextInput
                      fieldType={'time'}
                      fieldName={'toTime'}
                      formik={formik}
                      placeholder=''
                      isTooltipNotRequired={true}
                      isWidthNotRequired={false}
                      isDisabled={allDay}
                      isTooltipForModal
                    />
                  </div>
                  <div className='col-lg-2 col-md-2 col-xl-2 col-12 py-1'>
                    <SelectInput
                      fieldName={'timeZone'}
                      placeholder={formatMessage({ id: 'Select Timezone' })}
                      formik={formik}
                      options={timeZoneOptions}
                      isTooltipNotRequired={true}
                      isTooltipForModal
                    />
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col-12 col-lg-6 col-xl-6 col-md-6 mb-8'>
                  <div className='mx-2'>
                    <div>
                      <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                        {formatMessage({ id: 'Assign Host' })}*{' '}
                        <ToolTipUIForModal
                          tooltipText={formatMessage({
                            id: 'GLOBAL.TOOLTIP.ACTIVITY.ASSIGN_HOST',
                          })}
                        />
                      </label>

                      <Field
                        as='select'
                        name='assignHost'
                        defaultValue={currentUser?.userId}
                        className=' form-select h-40px font-size-13'
                      >
                        {allUsers?.map((user: any) => (
                          <option key={user.id} value={user.value}>
                            {user.name}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div>
                </div>
                <div className='col-12 col-lg-6 col-xl-6 col-md-6'>
                  <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                    {formatMessage({ id: 'Select Investor Users' })}*{' '}
                    <ToolTipUIForModal
                      tooltipText={formatMessage({
                        id: 'GLOBAL.TOOLTIP.ACTIVITY.INVESTOR_USER',
                      })}
                    />
                  </label>

                  <Select
                    classNamePrefix='react-select'
                    isLoading={isSearching}
                    value={selectedValue}
                    closeMenuOnSelect={false}
                    isMulti
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: errorInvestorUser ? 'red' : provided.borderColor,
                        backgroundColor: errorInvestorUser
                          ? 'rgba(241, 65, 108, 0.2)'
                          : provided.backgroundColor,
                      }),
                    }}
                    name='InvestorUser'
                    options={investorUser}
                    onChange={handleChangeInvestoruser}
                    isDisabled={isSearching}
                  />
                  <div className='mt-2 mb-4'>
                    {errorInvestorUser && (
                      <span className='text-danger font-size-13'> {errorInvestorUser}</span>
                    )}
                  </div>
                </div>
                <div
                  className={`col-12 col-lg-6 col-xl-6 col-md-6  ${type === 'documents' ? '' : 'd-none'
                    }`}
                >
                  <div className='mx-2 d-flex flex-column'>
                    <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                      {formatMessage({ id: 'Document' })}
                      <ToolTipUIForModal
                        tooltipText={formatMessage({
                          id: 'GLOBAL.TOOLTIP.ACTIVITY.DOCUMENT',
                        })}
                      />
                    </label>
                    <BasicButton
                      customClass='btn-light text-dark'
                      onClick={handleOpen}
                      buttonText={formatMessage({ id: 'Upload Document' })}
                      border='none'
                      width='225px'
                    />
                    {documentName ? (
                      <p className='mt-2'>{documentName} {formatMessage({ id: 'file uploaded successfully' })}</p>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
                <div
                  className={`col-12 col-lg-6 col-xl-6 col-md-6 ${type === 'tasks' || type === 'notes' || type === 'documents' ? 'd-none' : ''
                    }`}
                >
                  <div className='mx-2 '>
                    <TextInput
                      fieldType={'text'}
                      label={formatMessage({ id: 'Add Guests' })}
                      fieldName={'guest'}
                      placeholder={formatMessage({ id: 'Enter guests email address' })}
                      formik={formik}
                      isStarRequired={false}
                      toolTipText={formatMessage({
                        id: 'GLOBAL.TOOLTIP.ACTIVITY.ADD_GUESTS',
                      })}
                      isTooltipForModal
                    />
                  </div>
                </div>
                <div
                  className={`col-12 col-lg-6 col-xl-6 col-md-6 ${type === 'tasks' || type === 'notes' || type === 'documents' ? 'd-none' : ''
                    }`}
                >
                  <div className='mx-2'>
                    <TextInput
                      fieldType={'text'}
                      label={formatMessage({ id: 'Meeting link' })}
                      fieldName={'meetingLink'}
                      placeholder={formatMessage({ id: 'Enter Meeting link' })}
                      formik={formik}
                      isStarRequired={false}
                      toolTipText={formatMessage({
                        id: 'GLOBAL.TOOLTIP.ACTIVITY.MEETING_LINK',
                      })}
                      isTooltipForModal
                    />
                  </div>
                </div>
                <div className='col-12 col-lg-6 col-xl-6 col-md-6'>
                  <div className='mx-2'>
                    <TextArea
                      label={formatMessage({ id: 'Add Notes' })}
                      fieldName={'meetingNotes'}
                      placeholder={formatMessage({
                        id: 'Enter notes',
                      })}
                      formik={formik}
                      isStarRequired={false}
                      toolTipText={formatMessage({
                        id: 'GLOBAL.TOOLTIP.ACTIVITY.MEETING_NOTES',
                      })}
                      isTooltipForModal
                    />
                  </div>
                </div>
              </div>

              <div className=' row'>
                <div className='d-flex flex-md-row flex-column-reverse justify-content-end me-md-6'>
                  <BasicButton
                    customClass='btn-light text-dark'
                    onClick={() => setModalShow(false)}
                    buttonText={formatMessage({ id: 'Cancel' })}
                    border='none'
                    minWidth={90}
                  />

                  <CustomButton
                    isSubmitting={formik.isSubmitting}
                    isValid={formik.isValid}
                    buttonText={formatMessage({ id: 'Add Activity' })}
                    loading={loading}
                    width={12}
                    marginButtom={'mb-2 mb-md-0 ms-md-3 ms-0'}
                    widthLoading={4}
                    height={44}

                  />
                  <FileUpload
                    fileSize={10485760}
                    maxFileNumber={1}
                    allowType={[
                      'application/pdf',
                      'application/msword',
                      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    ]}
                    metaData={{ module: 'document', isProtected: true, companyId: companyId }}
                    modalStatus={modelStatus}
                    handleClose={handleClose}
                    handleSuccess={(id: number, name: string) => {
                      formik.setFieldValue('documentId', id)
                      setDocumentName(name.split('-')[0])
                    }}
                    resourceType='all'
                  />
                </div>
                {/* </div> */}
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}
